import type { FC, ReactNode } from 'react'
import type { FollowUpBucket, ReminderStatus, ReminderTone } from '@/lib/clinic/selectors'

import {
    BellOff,
    CircleCheck,
    MessageCircle,
    Phone,
    PhoneMissed
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { dateFromTimestamp, formatDayMonth } from '@/lib/clinic/dates'

interface PillProps {
    className?: string
    children: ReactNode
}

export const Pill: FC<PillProps> = ({ className, children }): ReactNode => {
    return (
        <span
            className={cn(
                'inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset [&_svg]:size-3.5',
                className
            )}
        >
            {children}
        </span>
    )
}

const BUCKET_STYLES: Record<FollowUpBucket, { label: string, className: string }> = {
    today: { label: 'Due today', className: 'bg-teal-50 text-teal-800 ring-teal-600/25' },
    tomorrow: { label: 'Tomorrow', className: 'bg-sky-50 text-sky-800 ring-sky-600/25' },
    upcoming: { label: 'Upcoming', className: 'bg-slate-50 text-slate-700 ring-slate-500/20' },
    overdue: { label: 'Overdue', className: 'bg-rose-50 text-rose-700 ring-rose-600/25' },
    completed: { label: 'Completed', className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/25' },
    cancelled: { label: 'Cancelled', className: 'bg-zinc-100 text-zinc-600 ring-zinc-500/20' }
}

interface FollowUpBadgeProps {
    bucket: FollowUpBucket
    className?: string
}

export const FollowUpBadge: FC<FollowUpBadgeProps> = ({ bucket, className }): ReactNode => {
    const style = BUCKET_STYLES[bucket]

    return (
        <Pill className={cn(style.className, className)}>
            {style.label}
        </Pill>
    )
}

const REMINDER_STYLES: Record<ReminderTone, string> = {
    none: 'bg-white text-muted-foreground ring-border',
    whatsapp: 'bg-emerald-50 text-emerald-700 ring-emerald-600/25',
    call: 'bg-sky-50 text-sky-700 ring-sky-600/25',
    warning: 'bg-amber-50 text-amber-800 ring-amber-600/30',
    confirmed: 'bg-teal-50 text-teal-800 ring-teal-600/25',
    muted: 'bg-zinc-100 text-zinc-600 ring-zinc-500/20'
}

const REMINDER_ICONS: Record<ReminderTone, typeof Phone | null> = {
    none: BellOff,
    whatsapp: MessageCircle,
    call: Phone,
    warning: PhoneMissed,
    confirmed: CircleCheck,
    muted: null
}

interface ReminderBadgeProps {
    status: ReminderStatus
    className?: string
}

export const ReminderBadge: FC<ReminderBadgeProps> = ({ status, className }): ReactNode => {
    const Icon = REMINDER_ICONS[status.tone]
    const when = status.at ? formatDayMonth(dateFromTimestamp(status.at)) : null

    return (
        <Pill className={cn(REMINDER_STYLES[status.tone], className)}>
            {Icon && <Icon aria-hidden='true' />}
            {status.label}
            {when && (
                <span className='font-normal opacity-80'>
                    · {when}
                </span>
            )}
        </Pill>
    )
}

interface VisitTypeBadgeProps {
    totalVisits: number
    className?: string
}

export const VisitTypeBadge: FC<VisitTypeBadgeProps> = ({ totalVisits, className }): ReactNode => {
    if (totalVisits <= 1) {
        return (
            <Pill className={cn('bg-sky-50 text-sky-700 ring-sky-600/25', className)}>
                New
            </Pill>
        )
    }

    return (
        <Pill className={cn('bg-teal-50 text-teal-800 ring-teal-600/25', className)}>
            Returning
        </Pill>
    )
}
